import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Search, X, Eye, Image } from 'lucide-react';
import { MenuSearch, SearchFilters } from '@/components/MenuSearch';
import { getApiUrl } from '@/config/api';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: string;
  category: string;
  imageUrl?: string;
  aiGenerated?: boolean;
  dietary_flags: { [key: string]: boolean };
  section_id: string;
  is_beverage?: boolean;
  is_modifier?: boolean;
}

interface MenuItemWithSection extends MenuItem {
  sectionName: string;
}

interface MenuSection {
  id: string;
  name: string;
  items: MenuItem[];
}

const MenuPreview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    sections: [],
    dietary: [],
    itemType: 'all',
    priceRange: {}
  });
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [menuSections, setMenuSections] = useState<MenuSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get menu_id or rest_id from URL params
  const menuId = searchParams.get('menu_id');
  const restId = searchParams.get('rest_id');

  // Fetch real menu data
  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        setLoading(true);
        
        let restaurantId = restId;
        
        // If we have menu_id but no rest_id, we need to get the restaurant ID first
        if (menuId && !restId) {
          // Get current user info to get rest_id
          // Use credentials: 'include' to send httpOnly auth cookies
          try {
            const userResponse = await fetch(getApiUrl('demo/v3/validate'), {
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            });

            if (userResponse.ok) {
              const userData = await userResponse.json();
              // Use the user's actual restaurant ID - check both user object and top level
              restaurantId = userData.user?.rest_id?.toString() ||
                            userData.user?.restaurant_id?.toString() ||
                            userData.rest_id?.toString() ||
                            userData.restaurant_id?.toString();
              console.log('Got restaurant ID from user data:', restaurantId, userData);
            }
          } catch (error) {
            console.log('Could not get user info, using fallback');
          }
        }
        
        // If no restaurant ID found, try to get it from localStorage as fallback
        if (!restaurantId) {
          const storedRestId = localStorage.getItem('current_rest_id');
          if (storedRestId) {
            restaurantId = storedRestId;
            console.log('Using restaurant ID from localStorage:', restaurantId);
          }
        }
        
        // If still no restaurant ID found, show error
        if (!restaurantId) {
          throw new Error('Restaurant ID not found. Please ensure you are logged in or provide rest_id in the URL.');
        }

        // SECURITY: If menu_id was provided in URL, validate ownership
        // Note: Authentication is already validated by UniversalProtectedRoute
        // We just need to verify the user owns this menu
        if (menuId) {
          try {
            // Validate that the menu belongs to current user's restaurant
            // Use credentials: 'include' to send httpOnly auth cookies
            const menuResponse = await fetch(getApiUrl(`menus/${menuId}/items`), {
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            });

            // If we get 404, it means either menu doesn't exist or user doesn't own it
            if (menuResponse.status === 404) {
              console.log('🔒 Menu access denied or not found - redirecting to 404');
              navigate('/404', { replace: true });
              return;
            }

            if (!menuResponse.ok) {
              // For other errors (500, etc), fall through to normal error handling
              throw new Error('Failed to validate menu access');
            }

            console.log('✅ Menu access validated - user owns this menu');
          } catch (err: any) {
            // If it's a navigation that already happened, don't throw
            if (err.message === 'Failed to validate menu access') {
              throw err; // Fall through to normal error handling
            }
            // For other errors, redirect to 404
            console.log('🔒 Error validating menu access - redirecting to 404');
            navigate('/404', { replace: true });
            return;
          }
        }

        // Use public endpoint that doesn't require authentication
        const response = await fetch(getApiUrl(`public/restaurants/${restaurantId}/menu`), {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch menu data');
        }

        const data = await response.json();
        
        // Group items by category (the public endpoint returns category, not section)
        const sectionsMap: { [key: string]: { name: string; items: MenuItem[] } } = {};
        
        data.forEach((item: any) => {
          const category = item.category || 'Other';
          const sectionId = category.toLowerCase().replace(/\s+/g, '-');
          
          if (!sectionsMap[sectionId]) {
            sectionsMap[sectionId] = {
              name: category,
              items: []
            };
          }
          
          sectionsMap[sectionId].items.push({
            id: item.menu_item_id.toString(),
            name: item.name,
            description: item.description || '',
            price: item.price.toFixed(2),
            category: item.category,
            // FIX: Prioritize user-uploaded images over AI-generated images
            imageUrl: item.uploaded_image_url || item.ai_image_url,
            aiGenerated: !item.uploaded_image_url && !!item.ai_image_url,
            dietary_flags: item.dietary_flags || {},
            section_id: sectionId,
            is_beverage: item.category?.toLowerCase().includes('beverage') || 
                        item.category?.toLowerCase().includes('drink') ||
                        item.category?.toLowerCase().includes('wine') ||
                        item.category?.toLowerCase().includes('cocktail') ||
                        item.category?.toLowerCase().includes('beer')
          });
        });

        // Convert to sections array and sort (food first, then beverages)
        const sections: MenuSection[] = Object.entries(sectionsMap)
          .map(([id, section]) => ({
            id,
            name: section.name,
            items: section.items
          }))
          .sort((a, b) => {
            const foodCategories = ['appetizers', 'salads', 'soups', 'entrees', 'burgers', 'sandwiches', 'pasta', 'seafood', 'steaks', 'desserts', 'kids', 'sides'];
            const aIsFood = a.items.some(item => foodCategories.includes(item.category));
            const bIsFood = b.items.some(item => foodCategories.includes(item.category));
            
            if (aIsFood && !bIsFood) return -1;
            if (!aIsFood && bIsFood) return 1;
            return 0;
          });

        setMenuSections(sections);
        
        // Default to first section
        if (sections.length > 0) {
          setSelectedSectionId(sections[0].id);
        }
        
      } catch (err) {
        console.error('Error fetching menu data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load menu data');
      } finally {
        setLoading(false);
      }
    };

    fetchMenuData();
  }, [menuId, restId, searchParams, navigate]);

  // Handle search with filters
  const handleSearch = useCallback((term: string, filters: SearchFilters) => {
    setSearchQuery(term);
    setSearchFilters(filters);
  }, []);

  // Get all items from all sections
  const allItems = menuSections.flatMap(section => 
    section.items.map(item => ({ ...item, sectionName: section.name }))
  );

  // Apply filters
  const getFilteredItems = (): MenuItemWithSection[] => {
    let filtered = allItems;

    // Apply search term
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.price.toString().includes(term)
      );
    }

    // Apply section filter
    if (searchFilters.sections.length > 0) {
      filtered = filtered.filter(item => searchFilters.sections.includes(item.section_id));
    }

    // Apply dietary filters with AND logic
    if (searchFilters.dietary.length > 0) {
      filtered = filtered.filter(item => {
        if (!item.dietary_flags || typeof item.dietary_flags !== 'object') return false;
        return searchFilters.dietary.every(dietary => 
          item.dietary_flags[dietary] === true
        );
      });
    }

    // Apply item type filter
    if (searchFilters.itemType !== 'all') {
      filtered = filtered.filter(item => {
        return searchFilters.itemType === 'beverage' ? item.is_beverage : !item.is_beverage;
      });
    }

    return filtered;
  };

  // Get currently selected section
  const selectedSection = menuSections.find(section => section.id === selectedSectionId) || menuSections[0];

  // Check if we're actively filtering
  const isFiltering = searchQuery.trim() || 
                     searchFilters.sections.length > 0 || 
                     searchFilters.dietary.length > 0 || 
                     searchFilters.itemType !== 'all';

  // If filtering, show filtered results; otherwise show selected section items
  const displayItems = isFiltering ? getFilteredItems() : 
    (selectedSection?.items.map(item => ({ ...item, sectionName: selectedSection.name })) || []);

  const ItemPreviewModal = ({ item, onClose }: { item: MenuItem; onClose: () => void }) => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl border border-gray-200"
      >
        <div className="relative aspect-video">
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <Image className="h-24 w-24 text-gray-300" />
            </div>
          )}
        </div>
        <div className="p-8">
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">{item.name}</h3>
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">{item.description}</p>
          <div className="flex justify-between items-center">
            <span className="text-3xl font-bold text-[#FDB913]">${item.price}</span>
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-200 transition-all font-medium text-base"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FDB913] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading menu preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <p className="text-gray-700 mb-4">Error: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-100 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-200 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Header - Light Mode */}
      <div className="border-b border-gray-200 bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/restaurant-dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              Live Menu Preview
            </h1>
            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      <div className="relative z-10">

        {/* Search with Filters */}
        <div className="container mx-auto px-4 py-4 max-w-6xl">
          <MenuSearch
            onSearch={handleSearch}
            sections={menuSections.map(s => ({ id: s.id, name: s.name }))}
            totalItems={allItems.length}
            filteredItems={displayItems.length}
          />
        </div>

        {/* Menu Content - Same Layout as Review */}
        <div className="container mx-auto px-4 pb-32">
          <div className="max-w-7xl mx-auto flex gap-6">
          {/* Left Sidebar - Section Navigation */}
          <div className="w-72 flex-shrink-0">
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Menu Sections</h3>
              <div className="space-y-2">
                {menuSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSectionId(section.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all border relative ${
                      selectedSectionId === section.id
                        ? 'bg-[#FDB913] border-[#FDB913] text-black shadow-sm'
                        : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    {selectedSectionId === section.id && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-black/20 rounded-l-lg"></div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">{section.name}</span>
                      <span className={`text-xs ${selectedSectionId === section.id ? 'text-black/70' : 'text-gray-500'}`}>
                        {section.items.length}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content - Selected Section Items */}
          <div className="flex-1">
            {selectedSection && (
              <motion.div
                key={selectedSection.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-8"
              >
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Section Header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-2xl font-semibold text-gray-900">
                          {isFiltering ? 'Filtered Results' : selectedSection.name}
                        </h2>
                        <p className="text-gray-500 text-sm">
                          {isFiltering
                            ? `Found ${displayItems.length} items matching your filters`
                            : `${displayItems.length} items`
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items - Same Style as Review */}
                  <div className="p-6">
                    <div>
                      {displayItems.map((item) => (
                        <div
                          key={item.id}
                          className="group relative bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-300 border border-gray-200 hover:border-gray-300 mb-3"
                        >
                          <div className="p-4">
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 leading-tight">{item.name}</h3>
                                {isFiltering && 'sectionName' in item && (
                                  <p className="text-xs text-gray-500 mt-0.5">in {item.sectionName}</p>
                                )}
                              </div>
                              <span className="text-[#FDB913] font-bold text-lg flex-shrink-0 ml-4">${item.price}</span>
                            </div>
                            {item.description && (
                              <p className="text-gray-600 text-sm leading-relaxed mb-3">{item.description}</p>
                            )}

                            {/* Preview button on hover */}
                            <div className="opacity-0 group-hover:opacity-100 transition-all duration-200">
                              <div className="flex gap-2 pt-3 mt-2 border-t border-gray-200">
                                <button
                                  onClick={() => setSelectedItem(item)}
                                  className="p-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-lg transition-all"
                                  title="Preview"
                                >
                                  <Eye className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {displayItems.length === 0 && (
                        <div className="text-center py-8">
                          <p className="text-gray-500">
                            {searchQuery.trim()
                              ? "No items found matching your search."
                              : "No items in this section."
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 z-40">
        <div className="container mx-auto max-w-6xl flex justify-between items-center">
          <p className="text-gray-500 text-sm">
            {allItems.length} items in this menu
          </p>
          <button
            onClick={() => navigate('/restaurant-dashboard')}
            className="bg-[#FDB913] text-black hover:bg-[#e5a711] font-bold px-8 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Item Preview Modal */}
      <AnimatePresence>
        {selectedItem && (
          <ItemPreviewModal
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MenuPreview;